// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadBargainBinProducts from "@awc/App/Handlers/BargainBin/GetBargainProducts";
import { BargainBinsCategoryModel } from "@awc/App/Services/BargainBin";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { BarginBinCategoriesLinks } from "@awc/App/Widgets/BargainBin/BargainBin";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    category: BargainBinsCategoryModel;
    level: number;
}

const mapDispatchToProps = {
    loadBargainBinProducts,
};
interface BargainBinCategoryAccordionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    productLink?: LinkPresentationProps;
    accordion?: AccordionPresentationProps;
}
export const bargainBinCategoryAccordionStyles: BargainBinCategoryAccordionStyles = {};
const styles = bargainBinCategoryAccordionStyles;

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const BargainBinCategoryAccordion = ({ category, loadBargainBinProducts, level }: Props) => {
    const { selectedProductId } = useAppSelector(state => ({
        selectedProductId: (state as CustomAppState).xBargain.selectedProductId,
    }));

    const selectProduct = (id: string) => {
        let urlSegment = window.location.pathname.split("?")[0];
        let bbCategory = "";
        if (urlSegment.split("?")[0] === BarginBinCategoriesLinks.Bulk) {
            bbCategory = "bulk";
        }

        // from classic, they only send category name if it was "bulk-deals"
        loadBargainBinProducts({
            productId: id,
            bargainBinCategory: bbCategory,
            isBargainBinPage: true,
        });
    };
    // render up to 3 levels
    if (level > 3) {
        return null;
    }
    return (
        <Accordion headingLevel={2} {...styles.accordion} className={`level${level}`}>
            <ManagedAccordionSection
                initialExpanded={false}
                key={category.categoryId}
                title={`${category.categoryName} ${
                    category.products.length > 0 ? ` - (${category.products.length})` : ``
                } `}
                {...styles.accordionSection}
            >
                {/* render subcategories */}
                {category.subCategories?.length > 0 &&
                    category.subCategories.map(subCategory => (
                        // eslint-disable-next-line react/jsx-no-undef
                        <BargainBinCategoryAccordion
                            loadBargainBinProducts={loadBargainBinProducts}
                            key={subCategory.categoryId}
                            category={subCategory}
                            level={level + 1}
                        />
                    ))}

                {category.products?.length > 0 && (
                    <ul>
                        {category.products.map(product => (
                            <li
                                key={product.id}
                                className={`product-link ${selectedProductId === product.id ? "selected" : ""}`}
                            >
                                <Link {...styles.productLink} onClick={() => selectProduct(product.id)}>
                                    {product.shortDescription}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default connect(null, mapDispatchToProps)(BargainBinCategoryAccordion);
